/**
 * Declarative client-side navigation for the dashboard shell.
 *
 * **Why this exists**
 * After removing `connected-react-router`, navigation must not be scattered as raw
 * `history.push` / `history.replace` calls inside thunks and helpers. Dispatching
 * `navigationIntent` keeps routing as a Redux-visible event so listener middleware
 * (e.g. in dashboard-ui) can perform the actual `history` call in one place—supporting
 * optional `state` (location state for worklist/cursor context) and `replace` without
 * coupling every package to the history singleton.
 *
 * **How to use**
 * - From app code: `dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: '/cmx/...', state: { ... } }))`
 *   or `launchPageActions.navigationIntent(...)` where `launchPageActions` re-exports this action.
 * - Callers can narrow `state` with `NavigationIntentPayload<YourState>` when
 *   typing payloads or dispatch helpers.
 * - Prefer semantic `intent` + `url` over hardcoding only when you need the predefined routes map;
 *   for case/detail navigation, `intent: 'CASE_DETAIL'` with `url` is typical.
 * - Pass `replace: true` when the current history entry should be replaced (e.g. redirects).
 * - The consuming app must register a listener that maps this action to `history.push` / `history.replace`.
 *
 * @see packages/dashboard-ui/src/store/listener-middleware.ts
 *
 * **Follow-up (CDMX-7018)** — When the Redux Toolkit migration is in place
 * (https://codametrix.atlassian.net/browse/CDMX-7018), revisit this action: align with RTK
 * listener middleware / `createAsyncThunk` patterns, consolidate navigation side effects in
 * one place, and consider semantic-only payloads (intent + entity ids) with URL construction
 * in middleware instead of passing raw paths from every caller.
 */
import { createAction } from '@reduxjs/toolkit';

export type NavigationIntent =
  | 'NOT_AUTHORIZED'
  | 'CASE_NOT_FOUND'
  | 'CASE_CHOOSER'
  | 'EULA'
  | 'SESSION_OPENED'
  | 'CONTEXT_UNAVAILABLE'
  | 'TIMED_OUT'
  | 'CASE_DETAIL';

export type NavigationIntentPayload<TState = unknown> = {
  intent: NavigationIntent;
  url?: string;
  state?: TState;
  replace?: boolean;
};

export const navigationIntent =
  createAction<NavigationIntentPayload>('NAVIGATION_INTENT');
